/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import Checkbox from '@material-ui/core/Checkbox';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';

import Button from '../../shared/_TBComponents/Button';
import {
  fetchExercises,
  fetchTimingGateExercises,
  setExercises,
  setIsNewDashboard,
  setIsOverview,
  setMaxType,
  setSCDashView,
} from './ducks/sportCoachDashboardActions';
import iconSet from '../../shared/images/teambuildr-selection.json';
import { dashboardTracker } from '../../shared/utils/amplitudeHelper';

const AvailableExercise = styled('div')`
  align-items: center;
  color: ${(props) => props.color};
  display: flex;
  justify-content: space-between;
  min-height: 50px;
  padding-left: 15px;
  .exercise-checkbox {
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  }
`;

const BackgroundContainer = styled('div')`
  background: white;
  border-radius: 8px;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const BoxTitle = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 16px;
  height: 25px;
  width: 100%;
`;

const ButtonContainer = styled('div')`
  display: flex;
  margin-bottom: 20px;
  margin-top: 20px;
  width: 100%;

  .load-button {
    background-color: #444444;
    border-radius: 4px;
  }
`;

const CenterContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 85%;
`;

const ExercisesBox = styled('div')`
  background: #fcfcfc;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 10px;
  overflow-y: scroll;
  padding: 15px;
  scrollbar-color: lightgrey transparent;
  scrollbar-width: thin;
`;

const Header = styled('div')`
  color: black;
  font-family: 'Nunito Sans';
  font-size: 23px;
  margin-top: 10px;
`;

const ExerciseTypeSelectContainer = styled('div')`
  margin-top: 10px;
  width: 100%;
`;

const LeftBox = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 48%;
`;

const LowerBox = styled('div')`
  display: flex;
  height: 500px;
  justify-content: space-between;
  margin-top: 20px;
  width: 100%;
`;

const NewContainer = styled('div')`
  display: flex;
  height: auto;
  padding: 20px;
  width: 100%;
`;

const PlusIconContainer = styled('div')`
  align-items: center;
  border: 1px solid black;
  border-radius: 50%;
  display: flex;
  height: 75px;
  justify-content: center;
  width: 75px;
`;

const RemoveContainer = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const RightBox = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 48%;
`;

const SearchAndFilter = styled('div')`
  display: flex;
  margin-top: 10px;
  width: 100%;
`;

const SearchAndIcon = styled('div')`
  align-items: center;
  background: white;
  border: 1px solid #eeeeee;
  border-radius: 50px;
  display: flex;
  overflow: hidden;
  transition: max-width 0.5s ease;
  width: 100%;
`;

const SearchContainer = styled('input')`
  background: none;
  border: none;
  display: flex;
  height: 40px;
  padding-left: 5px;
  width: 100%;
`;

const SearchIconContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-left: 10px;
  padding-right: 5px;
`;

const SelectBoxContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 35px;
  width: 100%;
`;

const SelectBoxText = styled('div')`
  color: ${(props) => props.color};
  display: flex;
  font-size: 16px;
  margin-bottom: 5px;
`;

const SubHeader = styled('div')`
  color: ${(props) => props.color};
  font-family: 'Nunito Sans';
  font-size: 16px;
`;

const SubHeaderContainer = styled('div')`
  color: black;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 16px;
  margin-top: 20px;
  width: 100%;
`;

const UpperContainer = styled('div')`
  align-items: center;
  border-bottom: 2px solid #eeeeee;
  display: flex;
  flex-direction: column;
  height: 200px;
  justify-content: center;
  width: 100%;
`;

const maxTypes = [
  { label: 'All maxes and PRs', id: 1 },
  { label: 'Only Manually Entered Maxes and PRs', id: 2 },
  { label: 'Only Generated Maxes and PRs', id: 3 },
];

const NewDashboardComponent = ({ selectedExercises, setSelectedExercises }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const availableExercises = useSelector(
    (state) => state.sportCoachDashboard.data.availableExercises,
  );
  const availableTimingGateExercises = useSelector(
    (state) => state.sportCoachDashboard.data.availableTimingGateExercises,
  );
  const maxType = useSelector(
    (state) => state.sportCoachDashboard.data.maxType,
  );

  const hasTimingGateExercises = availableTimingGateExercises.length > 0;

  const exerciseTabs = [
    { id: 0, name: 'Exercise Library', exercises: availableExercises },
    { id: 1, name: 'Timing Gate Exercises', exercises: availableTimingGateExercises },
  ];

  const [activeExerciseTab, setActiveExerciseTab] = useState(0);
  const [areChecksDisabled, setAreChecksDisabled] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedList, setSearchedList] = useState([]);

  const dispatch = useDispatch();
  const theme = useTheme();

  const sortedSelectedExercises = Array.isArray(selectedExercises) && selectedExercises.length
    ? [...selectedExercises].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const isExerciseSelected = (exercise) => {
    if ('id' in exercise && exercise.id !== null) {
      return selectedExercises.some((e) => e.id === exercise.id);
    }
    if ('displayName' in exercise && exercise.displayName !== null) {
      return selectedExercises.some((e) => e.displayName === exercise.displayName);
    }
    return false;
  };

  const createDashboardDisabled = (selectedExercisesArray) => !selectedExercisesArray.some(
    (exercise) => Object.keys(exercise).length > 0,
  );

  const handleExerciseRemove = (exercise) => {
    let newExercises;
    if ('id' in exercise && exercise.id !== null) {
      newExercises = selectedExercises.filter(
        (e) => !('id' in e) || e.id !== exercise.id,
      );
    } else if ('displayName' in exercise && exercise.displayName !== null) {
      newExercises = selectedExercises.filter(
        (e) => !('displayName' in e) || e.displayName !== exercise.displayName,
      );
    } else {
      newExercises = selectedExercises;
    }
    setSelectedExercises(newExercises);
  };

  const handleExerciseToggle = (exercise) => {
    if (isExerciseSelected(exercise)) {
      let newExercises;
      if ('id' in exercise && exercise.id !== null) {
        newExercises = selectedExercises.filter(
          (e) => !('id' in e) || e.id !== exercise.id,
        );
      } else if ('displayName' in exercise && exercise.displayName !== null) {
        newExercises = selectedExercises.filter(
          (e) => !('displayName' in e) || e.displayName !== exercise.displayName,
        );
      } else {
        newExercises = selectedExercises;
      }
      setSelectedExercises(newExercises);
    } else {
      setSelectedExercises([...selectedExercises, exercise]);
    }
  };

  const handleExerciseTabChange = (option) => {
    setActiveExerciseTab(option.id);
  };

  const handleMaxTypeChange = (option) => {
    dispatch(setMaxType(option.id));
  };

  const handleSearchChange = (inputValue) => {
    setSearchTerm(inputValue.target.value);
  };

  const handleSubmit = () => {
    const selectedDbExercises = selectedExercises.filter(
      (exercise) => Object.keys(exercise).length > 0 && !exercise.testType,
    );
    const selectedTimingGateExercises = selectedExercises.filter(
      (exercise) => Object.keys(exercise).length > 0 && !!exercise.testType,
    );
    const submissionExercises = selectedDbExercises.map((exercise) => ({
      id: exercise.id,
      inputType: exercise.inputType,
      name: exercise.name,
      type: exercise.type,
      unitAbbr: exercise.unitAbbr,
      unitName: exercise.unitName,
    }));

    const submissionTimingGateExercises = selectedTimingGateExercises.map((exercise) => {
      const {
        testType, name, displayName, distance, distanceUnit, unitAbbr,
      } = exercise;
      return {
        id: displayName,
        testType,
        name,
        displayName,
        distance,
        distanceUnit,
        unitAbbr,
      };
    });

    batch(() => {
      dispatch(setExercises(submissionExercises, submissionTimingGateExercises));
      dispatch(setIsNewDashboard(false));
      dispatch(setIsOverview(true));
      dispatch(setSCDashView('performance'));
    });
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'New Dashboard',
      'Load dashboard clicked',
    );
  };

  useEffect(() => {
    setSearchTerm('');
    setIsSearchFocused(false);
  }, [activeExerciseTab]);

  useEffect(() => {
    const exercises = exerciseTabs[activeExerciseTab]?.exercises ?? [];
    const filtered = exercises.filter(
      (exercise) => (exercise.displayName ?? exercise.name ?? '').toLowerCase().includes(searchTerm.trim().toLowerCase()),
    );
    setSearchedList(filtered);
  }, [activeExerciseTab, searchTerm, availableExercises, availableTimingGateExercises]);

  /**
   * Every time the list of selected exercises changes, the
   * component makes a new determination of whether or not
   * additional exercises can be selected. As we know, we
   * want a maximum of 10 exercises to exist in the selected
   * list. When an exercise is added to the object and then
   * subtracted, its entry is not deleted, but rather set to
   * an empty object. Therefore it's necessary to iterate over
   * a list of values representing that object and figure
   * if that value has length to determine whether or not the
   * exercise actually exists in the list.
   *
   * Once it's submitted, the object once again gets reduced
   * to a list and any entry that has a populated value gets
   * submitted in the list of exercises.
   */
  useEffect(() => {
    const validExercises = selectedExercises.filter(
      (value) => Object.keys(value).length > 0,
    ).length;
    setAreChecksDisabled(validExercises >= 10);
  }, [selectedExercises]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchExercises(currentUser.accountCode));
      dispatch(fetchTimingGateExercises(currentUser.accountCode));
    }
  }, [currentUser, dispatch]);

  const renderExerciseItem = (exercise) => {
    const isDisabled = areChecksDisabled && !isExerciseSelected(exercise);
    const displayName = exercise.displayName ?? exercise.name;
    return (
      <AvailableExercise
        color={theme.colors.text}
        disabled={isDisabled}
        key={exercise.id ?? exercise.displayName}
      >
        <div>{displayName}</div>
        <div>
          <Checkbox
            className='exercise-checkbox'
            checked={isExerciseSelected(exercise)}
            color='primary'
            disabled={isDisabled}
            onChange={() => handleExerciseToggle(exercise)}
          />
        </div>
      </AvailableExercise>
    );
  };

  const renderExerciseOptions = (exercise) => {
    if (Object.keys(exercise).length === 0) return null;

    return (
      <AvailableExercise color={theme.colors.text} key={exercise.id}>
        <div>{exercise.displayName ?? exercise.name}</div>
        <RemoveContainer
          aria-label={`Remove ${exercise.name} from selected exercises`}
          onClick={() => handleExerciseRemove(exercise)}
        >
          <IcomoonReact
            color='#9E9E9E'
            icon='remove'
            iconSet={iconSet}
            size={15}
          />
        </RemoveContainer>
      </AvailableExercise>
    );
  };

  const renderHeader = () => (
    <UpperContainer>
      <PlusIconContainer>
        <IcomoonReact color='black' icon='plus' iconSet={iconSet} size={25} />
      </PlusIconContainer>
      <Header>New Dashboard</Header>
    </UpperContainer>
  );

  const renderInstructions = () => (
    <SubHeaderContainer>
      <SubHeader color={theme.colors.darkGray}>
        Select the exercise results and up to 10 exercises to add to this
        dashboard.
      </SubHeader>
    </SubHeaderContainer>
  );

  const exerciseTypeOptions = exerciseTabs.map((tab) => ({ id: tab.id, label: tab.name }));

  const renderExerciseTypeSelector = () => (
    <ExerciseTypeSelectContainer>
      <Select
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.id}
        menuPortalTarget={document.body}
        onChange={handleExerciseTabChange}
        options={exerciseTypeOptions}
        styles={{
          control: (base) => ({ ...base, borderColor: '#eeeeee' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        value={exerciseTypeOptions.find((opt) => opt.id === activeExerciseTab)}
      />
    </ExerciseTypeSelectContainer>
  );

  const renderMaxTypeSelector = () => (
    <SelectBoxContainer>
      <SelectBoxText color={theme.colors.text}>Maxes and PRs</SelectBoxText>
      <Select
        defaultValue={maxTypes[0]}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.id}
        menuPortalTarget={document.body}
        onChange={handleMaxTypeChange}
        options={maxTypes}
        styles={{
          control: (base) => ({ ...base, borderColor: '#eeeeee' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        value={maxTypes.find((option) => option.id === maxType)}
      />
    </SelectBoxContainer>
  );

  const renderSearchBar = () => (
    <SearchAndFilter>
      <SearchAndIcon
        id='search-container'
        isFocused={isSearchFocused}
        onClick={() => setIsSearchFocused(true)}
      >
        <SearchIconContainer id='search-icon-container'>
          <IcomoonReact
            color='#9E9E9E'
            icon='magnifying-glass-alt'
            iconSet={iconSet}
            size={20}
          />
        </SearchIconContainer>
        <SearchContainer
          aria-label='Search exercises'
          id='search-container-input'
          onFocus={() => setIsSearchFocused(true)}
          onChange={handleSearchChange}
          placeholder='Search'
          value={searchTerm}
        />
      </SearchAndIcon>
    </SearchAndFilter>
  );

  const renderAllExercisesBox = () => (
    <LeftBox>
      <BoxTitle>All Exercises</BoxTitle>
      {renderSearchBar()}
      {hasTimingGateExercises && renderExerciseTypeSelector()}
      <ExercisesBox>
        {(!searchTerm.length ? exerciseTabs[activeExerciseTab].exercises : searchedList).map(
          renderExerciseItem,
        )}
      </ExercisesBox>
    </LeftBox>
  );

  const renderSelectedExercisesBox = () => (
    <RightBox>
      <BoxTitle>Selected Exercises</BoxTitle>
      <ExercisesBox>
        {sortedSelectedExercises.map(renderExerciseOptions)}
      </ExercisesBox>
    </RightBox>
  );

  const renderSubmitButton = () => (
    <ButtonContainer>
      <Button
        bgColor='#444444'
        bottom
        disabled={createDashboardDisabled(selectedExercises)}
        fontSize='14px'
        fullWidth
        large
        name='Load Dashboard'
        noBorder
        onClick={handleSubmit}
        rounded
        styledTextProps={{ fontWeight: '900' }}
        txtColor='#FFFFFF'
        upperCase
      />
    </ButtonContainer>
  );

  return (
    <NewContainer>
      <BackgroundContainer>
        <CenterContainer>
          {renderHeader()}
          {renderInstructions()}
          {renderMaxTypeSelector()}
          <LowerBox>
            {renderAllExercisesBox()}
            {renderSelectedExercisesBox()}
          </LowerBox>
          {renderSubmitButton()}
        </CenterContainer>
      </BackgroundContainer>
    </NewContainer>
  );
};

export default NewDashboardComponent;
